import type { AcceptableValue } from "reka-ui";
import type { SelectGroup, SelectItem } from "./select-items";

export type SelectGroupProps<T extends AcceptableValue> = {
	groupBy?: keyof T & string;
	groupNameResolver?: (header: string) => string;
};

export function useItemsGroups<T extends AcceptableValue>(props: SelectGroupProps<T>) {
	return (items: SelectItem<T>[]) => {
		const groups = groupBy(items, (item) => {
			if (!props.groupBy || item.item == null || typeof item.item !== "object") {
				return "";
			}

			return item.item[props.groupBy];
		});

		const groupItems: SelectGroup<T>[] = [];

		for (const group of groups) {
			groupItems.push({
				type: "group",
				label: props.groupNameResolver ? props.groupNameResolver(group.group) : group.group,
				items: group.items,
			});
		}

		return groupItems;
	};
}

type GroupedItems<T> = {
	group: string;
	items: T[];
};

function groupBy<T>(items: T[], keyGetter: (item: T) => string): GroupedItems<T>[] {
	const map = new Map<string, T[]>();
	for (const item of items) {
		const key = keyGetter(item);
		let collection = map.get(key);

		if (!collection) {
			collection = [];
			map.set(key, collection);
		}

		collection.push(item);
	}

	return Array.from(map.entries()).map(([group, items]) => ({
		group,
		items,
	}));
}
